import { useState } from "react";
import type { ReactElement, ReactNode } from "react";

import { Icon } from "../atoms/Icon";
import { readPanelOpen, writePanelOpen } from "../panelMemory";
import styles from "./CollapsiblePanel.module.css";

export interface CollapsiblePanelProps {
  readonly id: string;
  readonly title: string;
  readonly defaultOpen?: boolean | undefined;
  /**
   * Render the children only once the panel has been open, and keep them
   * afterwards. For content that fetches: a closed panel asks for nothing.
   */
  readonly lazy?: boolean | undefined;
  readonly children: ReactNode;
}

/**
 * A disclosure with a real button and a real `aria-expanded`.
 *
 * The region is always in the document and is hidden with the `hidden`
 * attribute rather than unmounted, so `aria-controls` always points at
 * something that exists — an identifier that resolves to nothing announces
 * worse than no identifier at all.
 *
 * The open state is remembered per panel id in `localStorage` (F-UIX-005, see
 * `panelMemory.ts`), and a storage that throws only means it is not remembered.
 */
export function CollapsiblePanel({
  id,
  title,
  defaultOpen = false,
  lazy = false,
  children,
}: CollapsiblePanelProps): ReactElement {
  // What the person left wins over what the caller prefers (F-UIX-005).
  const [open, setOpen] = useState<boolean>(() => readPanelOpen(id) ?? defaultOpen);
  const [everOpened, setEverOpened] = useState(open);

  return (
    <section className={styles.panel}>
      <button
        type="button"
        className={styles.trigger}
        aria-expanded={open}
        aria-controls={id}
        onClick={() => {
          setOpen(!open);
          if (!open) setEverOpened(true);
          writePanelOpen(id, !open);
        }}
      >
        <span className={open ? styles.chevronOpen : undefined}>
          <Icon name="chevron-down" />
        </span>
        {title}
      </button>
      <div id={id} className={styles.region} hidden={!open}>
        {lazy && !everOpened ? null : children}
      </div>
    </section>
  );
}
